import {expenseExportColumns as expenseExportColumnsContract} from '../../api';
import {localStorageKeysConstants} from '../../constants/localStorage.constants';

const {EXPENSE_EXPORT_COLUMNS} = localStorageKeysConstants;

const ALL_COLUMNS = expenseExportColumnsContract.columns;
const ALL_KEYS = new Set(ALL_COLUMNS.map((column) => column.key));
const LOCKED_COLUMNS = ALL_COLUMNS.filter((column) => column.locked);
const SELECTABLE_COLUMNS = ALL_COLUMNS.filter((column) => !column.locked);
const DEFAULT_KEYS = Object.freeze(SELECTABLE_COLUMNS.filter((column) => column.default).map((column) => column.key));

const sameKeySet = (keys, otherKeys) => {
  if (keys.length !== otherKeys.length) return false;
  const otherSet = new Set(otherKeys);
  return keys.every((key) => otherSet.has(key));
};

/**
 * Sibling of exportColumns.helpers.js, same shape -- the Expenses export wizard's own column
 * contract (expense-export-columns.json, generated from lib-backend-shared's
 * exports/expense_list.py:EXPENSE_LIST_COLUMNS) is independent of the Transactions wizard's
 * (export-columns.json), so the two never share a saved selection or a locked/selectable split.
 * Unlike the banking statement, every eligible expense column defaults on (the backend's own
 * declaration: "this export shows every eligible column by default").
 */
export const expenseExportColumnsHelpers = {
  lockedColumns: LOCKED_COLUMNS,
  selectableColumns: SELECTABLE_COLUMNS,
  defaultKeys: DEFAULT_KEYS,

  /** Whether `keys` is exactly the standard default set, order ignored. */
  isDefaultSet: (keys) => sameKeySet(keys, DEFAULT_KEYS),

  /** Set equality, order ignored. */
  sameKeySet,

  /**
   * The saved set for this company, or the standard default when nothing is saved for it (or
   * storage is unreadable). Drops any key the contract no longer declares, the same rule
   * exportColumns.helpers.js applies.
   */
  getSavedColumns: (companyId) => {
    try {
      const stored = JSON.parse(localStorage.getItem(EXPENSE_EXPORT_COLUMNS) || 'null');
      if (!companyId || stored?.companyId !== companyId || !Array.isArray(stored?.keys)) {
        return {keys: DEFAULT_KEYS, droppedKeys: []};
      }
      return {
        keys: stored.keys.filter((key) => ALL_KEYS.has(key)),
        droppedKeys: stored.keys.filter((key) => !ALL_KEYS.has(key)),
      };
    } catch (error) {
      return {keys: DEFAULT_KEYS, droppedKeys: []};
    }
  },

  saveColumns: (companyId, keys) => {
    if (!companyId) return;
    try {
      localStorage.setItem(EXPENSE_EXPORT_COLUMNS, JSON.stringify({companyId, keys}));
    } catch (error) {
      // Storage unavailable (private mode, quota): the selection simply isn't remembered.
    }
  },

  resetColumns: () => {
    try {
      localStorage.removeItem(EXPENSE_EXPORT_COLUMNS);
    } catch (error) {
      // Nothing to reset when storage is unavailable.
    }
  },
};
